<template>
  <v-card class="filters-card" flat>
    <!-- Header -->
    <v-card-title
        class="filters-header"
        @click="toggleFiltros"
    >
      <div class="d-flex align-center">
        <div class="icon-wrapper" :class="{ 'icon-active': filtrosExpandidos }">
          <v-icon size="20">
            {{ filtrosExpandidos ? 'mdi-filter-variant' : 'mdi-filter-variant-plus' }}
          </v-icon>
        </div>
        <div class="ml-3">
          <span class="filters-title">{{ titulo }}</span>
          <div class="filters-subtitle" v-if="hasCustomFilters">
            {{ totalFiltrosAtivos }} filtro{{ totalFiltrosAtivos > 1 ? 's' : '' }} ativo{{ totalFiltrosAtivos > 1 ? 's' : '' }}
          </div>
        </div>
      </div>

      <v-btn
          text
          small
          @click.stop="toggleFiltros"
          class="collapse-btn"
          :disabled="loading"
      >
        <v-icon small :class="{ 'rotate-icon': filtrosExpandidos }">
          mdi-chevron-down
        </v-icon>
        {{ filtrosExpandidos ? 'Recolher' : 'Expandir' }}
      </v-btn>
    </v-card-title>

    <!-- Painel de Filtros -->
    <v-expand-transition>
      <div v-show="filtrosExpandidos">
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <v-row dense>
            <!-- Seletor de Período -->
            <v-col
                v-if="mostrarPeriodo"
                cols="12"
                :md="mostrarFiliais ? 6 : 12"
            >
              <div class="filter-field-wrapper">
                <label class="field-label">
                  <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                  {{ labelPeriodo }}
                  <v-chip
                      v-if="periodo.inicio || periodo.fim"
                      x-small
                      color="primary"
                      text-color="white"
                      class="ml-2 status-chip"
                  >
                    <v-icon x-small>mdi-check</v-icon>
                  </v-chip>
                </label>
                <SeletorPeriodo
                    v-model="periodo"
                    :disabled="loading"
                    @change="onPeriodoChange"
                />
              </div>
            </v-col>

            <!-- Filiais -->
            <v-col
                v-if="mostrarFiliais"
                cols="12"
                :md="mostrarPeriodo ? 6 : 12"
            >
              <div class="filter-field-wrapper">
                <label class="field-label">
                  <v-icon small class="mr-1">mdi-domain</v-icon>
                  {{ labelFiliais }}
                  <v-chip
                      v-if="filialsSelecionadas.length > 0"
                      x-small
                      color="primary"
                      text-color="white"
                      class="ml-2 status-chip"
                  >
                    {{ filialsSelecionadas.length }}
                  </v-chip>
                </label>
                <SeletorFiliais
                    v-model="filialsSelecionadas"
                    :filiais="opcoesFiliais"
                    :minha-filial="minhaFilial"
                    :disabled="loading"
                    :shortcuts="atalhosFiliaisAtivos"
                />
              </div>
            </v-col>

            <!-- Slot para filtros adicionais -->
            <slot name="filtros-adicionais" :loading="loading"></slot>
          </v-row>
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-divider></v-divider>

    <!-- Summary de Filtros -->
    <v-card-text class="pa-4">
      <div class="filters-summary">
        <div class="filters-summary-content">
          <!-- Header do Summary -->
          <div class="summary-header">
            <div class="d-flex align-center">
              <div class="summary-icon-wrapper">
                <v-icon small :color="hasCustomFilters ? '#10b981' : '#64748b'">
                  {{ hasCustomFilters ? 'mdi-filter-check' : 'mdi-filter-outline' }}
                </v-icon>
              </div>
              <span class="summary-title ml-2">
                {{ hasCustomFilters ? 'Filtros Selecionados:' : 'Nenhum Filtro Aplicado' }}
              </span>
            </div>

            <!-- Ações (Desktop) -->
            <div class="filters-summary-actions d-none d-sm-flex">
              <v-btn
                  v-if="hasCustomFilters"
                  small
                  text
                  color="error"
                  @click="resetarFiltros"
                  :disabled="loading"
                  class="action-btn-clear"
              >
                <v-icon small left>mdi-filter-remove</v-icon>
                Limpar
              </v-btn>
              <v-btn
                  small
                  depressed
                  :color="hasCustomFilters ? 'primary' : ''"
                  :outlined="!hasCustomFilters"
                  @click="aplicarFiltros"
                  :loading="loading"
                  :disabled="loading || !hasCustomFilters"
                  class="action-btn-search"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Buscar
              </v-btn>
            </div>
          </div>

          <!-- Chips de Filtros Ativos -->
          <v-expand-transition>
            <div v-show="hasCustomFilters" class="chips-container">
              <v-chip-group column class="chips-wrapper">
                <!-- Chip Período -->
                <v-chip
                    v-if="mostrarPeriodo && (periodo.inicio || periodo.fim)"
                    small
                    outlined
                    color="#023449"
                    class="summary-chip"
                    close
                    @click:close="limparPeriodo"
                >
                  <v-icon small left class="chip-icon">mdi-calendar-range</v-icon>
                  <span class="chip-label">{{ periodLabel }}:</span>
                  <span class="chip-value">{{ formattedPeriodMobile }}</span>
                </v-chip>

                <!-- Chip Filiais -->
                <v-chip
                    v-if="mostrarFiliais && filialsSelecionadas.length > 0"
                    small
                    outlined
                    color="#023449"
                    class="summary-chip"
                    close
                    @click:close="limparFiliais"
                >
                  <v-icon small left class="chip-icon">mdi-domain</v-icon>
                  <span class="chip-label">{{ labelFiliais }}:</span>
                  <span class="chip-value">{{ filiaisChipLabel }}</span>
                </v-chip>

                <!-- Slot para chips adicionais -->
                <slot name="chips-adicionais"></slot>
              </v-chip-group>
            </div>
          </v-expand-transition>

          <!-- Ações (Mobile) -->
          <div class="filters-summary-actions-mobile d-flex d-sm-none">
            <v-btn
                v-if="hasCustomFilters"
                small
                text
                color="error"
                @click="resetarFiltros"
                :disabled="loading"
                class="action-btn-clear flex-grow-1"
            >
              <v-icon small left>mdi-filter-remove</v-icon>
              Limpar
            </v-btn>
            <v-btn
                small
                depressed
                :color="hasCustomFilters ? 'primary' : ''"
                :outlined="!hasCustomFilters"
                @click="aplicarFiltros"
                :loading="loading"
                :disabled="loading || !hasCustomFilters"
                class="action-btn-search flex-grow-1"
            >
              <v-icon small left>mdi-magnify</v-icon>
              Buscar
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import SeletorPeriodo from '../Inputs/SeletorPeriodo.vue';
import SeletorFiliais from '../Inputs/SeletorFiliais.vue';

export default {
  name: 'PainelFiltros',

  components: {
    SeletorPeriodo,
    SeletorFiliais
  },

  props: {
    // Título do card de filtros
    titulo: {
      type: String,
      default: 'Filtros de Busca'
    },

    // Estado de loading
    loading: {
      type: Boolean,
      default: false
    },

    // ===== CONFIGURAÇÃO PERÍODO =====
    mostrarPeriodo: {
      type: Boolean,
      default: true
    },
    labelPeriodo: {
      type: String,
      default: 'Período'
    },
    periodoInicial: {
      type: Object,
      default: () => ({ inicio: null, fim: null })
    },

    // ===== CONFIGURAÇÃO FILIAIS =====
    mostrarFiliais: {
      type: Boolean,
      default: true
    },
    labelFiliais: {
      type: String,
      default: 'Filiais'
    },
    opcoesFiliais: {
      type: Array,
      default: () => []
    },
    filiaisIniciais: {
      type: Array,
      default: () => []
    },
    minhaFilial: {
      type: Number,
      default: null
    },
    atalhosFiliaisAtivos: {
      type: Array,
      default: () => ['todas', 'minha', 'lojas', 'depositos']
    },

    // ===== CONFIGURAÇÕES GERAIS =====
    // Key para salvar estado no localStorage
    storageKey: {
      type: String,
      default: null
    },
    // Iniciar expandido ou recolhido
    iniciarExpandido: {
      type: Boolean,
      default: true
    },
    // Callback para validação customizada de filtros ativos
    validadorFiltrosCustomizados: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      periodo: { ...this.periodoInicial },
      filialsSelecionadas: [...this.filiaisIniciais],
      filtrosExpandidos: this.iniciarExpandido
    };
  },

  computed: {
    hasCustomFilters() {
      const dateChanged = this.mostrarPeriodo && (this.periodo.inicio !== null || this.periodo.fim !== null);
      const filiaisChanged = this.mostrarFiliais && this.filialsSelecionadas.length > 0;

      // Se houver validador customizado, também considera
      const customChanged = this.validadorFiltrosCustomizados
          ? this.validadorFiltrosCustomizados()
          : false;

      return dateChanged || filiaisChanged || customChanged;
    },

    totalFiltrosAtivos() {
      let count = 0;
      if (this.mostrarPeriodo && (this.periodo.inicio || this.periodo.fim)) count++;
      if (this.mostrarFiliais && this.filialsSelecionadas.length > 0) count++;
      return count;
    },

    formattedPeriod() {
      if (!this.periodo.inicio && !this.periodo.fim) return 'Não definido';
      if (this.periodo.inicio && this.periodo.fim) {
        return `${this.formatDate(this.periodo.inicio)} até ${this.formatDate(this.periodo.fim)}`;
      }
      if (this.periodo.inicio) return `A partir de ${this.formatDate(this.periodo.inicio)}`;
      return `Até ${this.formatDate(this.periodo.fim)}`;
    },

    formattedPeriodMobile() {
      if (!this.periodo.inicio && !this.periodo.fim) return 'Não definido';
      if (this.periodo.inicio && this.periodo.fim) {
        return `${this.formatDateShort(this.periodo.inicio)} - ${this.formatDateShort(this.periodo.fim)}`;
      }
      if (this.periodo.inicio) return `Desde ${this.formatDateShort(this.periodo.inicio)}`;
      return `Até ${this.formatDateShort(this.periodo.fim)}`;
    },

    periodLabel() {
      if (!this.periodo.inicio && !this.periodo.fim) return this.labelPeriodo;

      const hoje = new Date().toISOString().split('T')[0];

      if (this.periodo.inicio === hoje && this.periodo.fim === hoje) {
        return 'Hoje';
      }

      const ontem = new Date();
      ontem.setDate(ontem.getDate() - 1);
      const ontemStr = ontem.toISOString().split('T')[0];
      if (this.periodo.inicio === ontemStr && this.periodo.fim === ontemStr) {
        return 'Ontem';
      }

      const sete = new Date();
      sete.setDate(sete.getDate() - 7);
      const seteStr = sete.toISOString().split('T')[0];
      if (this.periodo.inicio === seteStr && this.periodo.fim === hoje) {
        return 'Últimos 7 dias';
      }

      const trinta = new Date();
      trinta.setDate(trinta.getDate() - 30);
      const trintaStr = trinta.toISOString().split('T')[0];
      if (this.periodo.inicio === trintaStr && this.periodo.fim === hoje) {
        return 'Últimos 30 dias';
      }

      return this.labelPeriodo;
    },

    filiaisChipLabel() {
      if (this.filialsSelecionadas.length === this.opcoesFiliais.length) {
        return `Todas (${this.filialsSelecionadas.length})`;
      }
      if (this.filialsSelecionadas.length <= 2) {
        return this.filialsSelecionadas
            .map(id => this.opcoesFiliais.find(f => f.idfilial === id)?.filial)
            .filter(Boolean)
            .join(', ');
      }
      return `${this.filialsSelecionadas.length} selecionada${this.filialsSelecionadas.length > 1 ? 's' : ''}`;
    }
  },

  watch: {
    filtrosExpandidos(val) {
      if (this.storageKey) {
        localStorage.setItem(`${this.storageKey}_expandidos`, val);
      }
    },

    periodoInicial: {
      handler(novoValor) {
        this.periodo = { ...novoValor };
      },
      deep: true
    },

    filiaisIniciais: {
      handler(novoValor) {
        this.filialsSelecionadas = [...novoValor];
      },
      deep: true
    }
  },

  mounted() {
    this.carregarEstadoLocalStorage();
  },

  methods: {
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },

    formatDateShort(date) {
      if (!date) return '';
      const [, month, day] = date.split('-');
      return `${day}/${month}`;
    },

    carregarEstadoLocalStorage() {
      if (!this.storageKey) return;

      const expandidosStorage = localStorage.getItem(`${this.storageKey}_expandidos`);
      if (expandidosStorage !== null) {
        this.filtrosExpandidos = expandidosStorage === 'true';
      }
    },

    toggleFiltros() {
      if (!this.loading) {
        this.filtrosExpandidos = !this.filtrosExpandidos;
      }
    },

    onPeriodoChange(periodo) {
      this.$emit('periodo-change', periodo);
    },

    limparFiliais() {
      this.filialsSelecionadas = [];
      this.$emit('filiais-change', []);
    },

    limparPeriodo() {
      this.periodo = { inicio: null, fim: null };
      this.$emit('periodo-change', { inicio: null, fim: null });
    },

    aplicarFiltros() {
      const filtros = {};

      if (this.mostrarPeriodo) {
        filtros.dataInicio = this.periodo.inicio;
        filtros.dataFim = this.periodo.fim;
      }

      if (this.mostrarFiliais) {
        filtros.filiais = this.filialsSelecionadas;
      }

      this.$emit('filtrar', filtros);
    },

    resetarFiltros() {
      if (this.mostrarPeriodo) {
        this.periodo = { inicio: null, fim: null };
      }

      if (this.mostrarFiliais) {
        this.filialsSelecionadas = [];
      }

      this.$emit('limpar');
    },

    // Métodos públicos para controle externo
    setPeriodo(periodo) {
      this.periodo = { ...periodo };
    },

    setFiliais(filiais) {
      this.filialsSelecionadas = [...filiais];
    },

    getPeriodo() {
      return { ...this.periodo };
    },

    getFiliais() {
      return [...this.filialsSelecionadas];
    }
  }
};
</script>

<style scoped>
/* ===== CARD PRINCIPAL ===== */
.filters-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.filters-card:hover {
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.12);
  border-color: #cbd5e1;
}

/* ===== HEADER ===== */
.filters-header {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px !important;
}

.filters-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: linear-gradient(180deg, #023449 0%, #0369a1 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.filters-header:hover::before {
  opacity: 1;
}

.filters-header:hover {
  background-color: rgba(2, 52, 73, 0.02);
}

.icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(2, 52, 73, 0.08);
  transition: all 0.3s ease;
}

.icon-wrapper >>> .v-icon {
  color: #023449 !important;
}

.icon-active {
  background: linear-gradient(135deg, #023449 0%, #0369a1 100%);
  box-shadow: 0 4px 10px rgba(2, 52, 73, 0.2);
}

.icon-active >>> .v-icon {
  color: #ffffff !important;
}

.filters-title {
  font-size: 1.063rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.filters-subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: #10b981;
  margin-top: 2px;
  letter-spacing: 0;
}

.collapse-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0;
  border-radius: 8px;
  border: 2px solid #cbd5e1 !important;
  padding: 0 16px !important;
  height: 32px !important;
  transition: all 0.2s ease;
  background: transparent !important;
}

.collapse-btn:hover:not(.v-btn--disabled) {
  background: #023449 !important;
  border-color: #023449 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.2);
}

.rotate-icon {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

/* ===== CAMPOS DE FILTRO ===== */
.filter-field-wrapper {
  position: relative;
}

.field-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #023449;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
  height: 24px;
}

.status-chip {
  height: 20px !important;
  font-size: 0.688rem;
  font-weight: 700;
}

/* ===== SUMMARY DE FILTROS ===== */
.filters-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.filters-summary:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.06);
}

.filters-summary-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.summary-icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.summary-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
}

.chips-container {
  width: 100%;
}

.chips-wrapper {
  margin: 0 !important;
  padding: 0 !important;
}

.chips-wrapper >>> .v-slide-group__content {
  gap: 8px;
  flex-direction: column;
  align-items: stretch;
}

.chips-wrapper >>> .v-chip {
  margin: 0 !important;
}

.chips-wrapper >>> .v-chip + .v-chip {
  margin-left: 0 !important;
}

.filters-summary-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.filters-summary-actions-mobile {
  width: 100%;
  gap: 8px;
  margin-top: 4px;
}

.action-btn-clear {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0;
  border-radius: 8px;
  height: 32px !important;
  padding: 0 14px !important;
  transition: all 0.2s ease;
}

.action-btn-clear:hover:not(.v-btn--disabled) {
  background-color: rgba(220, 38, 38, 0.08) !important;
  transform: translateY(-1px);
}

.action-btn-search {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0;
  border-radius: 8px;
  height: 32px !important;
  padding: 0 16px !important;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.2) !important;
}

.action-btn-search:hover:not(.v-btn--disabled) {
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.3) !important;
  transform: translateY(-1px);
}

/* ===== CHIPS DE RESUMO ===== */
.summary-chip {
  border-width: 1.5px !important;
  font-weight: 600;
  padding: 0 8px 0 4px !important;
  height: 28px !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.summary-chip >>> .v-chip__content {
  display: flex !important;
  align-items: center !important;
  flex: 1 !important;
  overflow: hidden !important;
  padding-right: 0 !important;
  padding-left: 8px !important;
}

.summary-chip:hover {
  background-color: rgba(2, 52, 73, 0.04) !important;
  transform: translateY(-1px);
}

.summary-chip >>> .v-chip__close {
  font-size: 18px !important;
  width: 20px !important;
  height: 20px !important;
  margin-left: auto !important;
  margin-right: 0 !important;
  opacity: 0.6;
  transition: all 0.2s ease;
  flex-shrink: 0 !important;
  position: relative !important;
  right: 0 !important;
}

.summary-chip >>> .v-chip__close:hover {
  opacity: 1;
  background-color: rgba(2, 52, 73, 0.12);
  border-radius: 50%;
}

.chip-icon {
  margin-right: 4px;
  flex-shrink: 0;
}

.chip-label {
  font-weight: 700;
  margin-right: 4px;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.chip-value {
  font-weight: 600;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== RESPONSIVIDADE ===== */
@media (min-width: 600px) {
  .chips-wrapper >>> .v-slide-group__content {
    flex-direction: row;
    align-items: center;
  }

  .chips-wrapper >>> .v-chip + .v-chip {
    margin-top: 0 !important;
  }
}

@media (max-width: 960px) {
  .filters-header {
    padding: 12px 16px !important;
  }

  .filters-title {
    font-size: 1rem;
  }

  .summary-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 599px) {
  .filters-card {
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .filters-header {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px !important;
  }

  .collapse-btn {
    width: 100%;
    height: 28px !important;
    font-size: 0.813rem;
  }

  .filters-summary {
    padding: 12px;
    border-radius: 8px;
  }

  .filters-summary-content {
    gap: 12px;
  }

  .summary-title {
    font-size: 0.813rem;
  }

  .chips-container {
    margin-top: 4px;
  }

  .summary-chip {
    height: 32px !important;
    font-size: 0.75rem;
    padding: 0 8px 0 4px !important;
    width: 100%;
  }

  .summary-chip >>> .v-chip__content {
    flex: 1 !important;
    padding-left: 8px !important;
  }

  .chip-icon {
    font-size: 16px !important;
    margin-right: 6px;
  }

  .chip-label {
    font-size: 0.75rem;
    font-weight: 600;
  }

  .chip-value {
    font-size: 0.75rem;
    flex: 1;
    text-align: left;
    margin-left: 4px;
  }

  .summary-chip >>> .v-chip__close {
    font-size: 20px !important;
    width: 24px !important;
    height: 24px !important;
    margin-left: 8px !important;
    margin-right: 0 !important;
    opacity: 0.7;
  }

  .summary-chip >>> .v-chip__close:hover {
    opacity: 1;
    background-color: rgba(2, 52, 73, 0.1);
    border-radius: 50%;
  }

  .action-btn-clear,
  .action-btn-search {
    height: 38px !important;
    font-size: 0.875rem;
    padding: 0 16px !important;
  }

  .action-btn-clear >>> .v-icon,
  .action-btn-search >>> .v-icon {
    font-size: 18px !important;
  }

  .filters-summary-actions-mobile {
    margin-top: 0;
  }
}

@media (max-width: 360px) {
  .filters-header {
    padding: 10px !important;
  }

  .icon-wrapper {
    padding: 8px;
  }

  .filters-title {
    font-size: 0.938rem;
  }

  .filters-subtitle {
    font-size: 0.688rem;
  }

  .summary-chip {
    height: 30px !important;
    font-size: 0.688rem;
    padding: 0 6px 0 4px !important;
  }

  .summary-chip >>> .v-chip__content {
    padding-left: 8px !important;
  }

  .chip-icon {
    font-size: 14px !important;
  }

  .chip-label,
  .chip-value {
    font-size: 0.688rem;
  }

  .summary-chip >>> .v-chip__close {
    font-size: 18px !important;
    width: 22px !important;
    height: 22px !important;
  }

  .action-btn-clear,
  .action-btn-search {
    height: 36px !important;
    font-size: 0.813rem;
  }
}

/* ===== ACESSIBILIDADE ===== */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>